// PIN on the web, client side only (a server-checked PIN or passkeys are the real fix).
//
// One PIN per user: src/services/pin.ts keeps every PIN entry under the signed-in user's id (setPINScope), so
// a browser holds a PIN for each user who set one, and another user's PIN never unlocks the app. Before, there
// was one PIN per browser: after a sign-out the next user got "Enter PIN" and passed with the previous user's.
//
// Unlock across reloads: in the app the PIN is asked on a cold start, and on the web every page reload is one —
// so the unlock is remembered in sessionStorage (this tab or home-screen app only, gone when it is closed), for
// the user who unlocked, and honoured while they have been active within the idle-logout window.
import { Platform } from 'react-native';
import { IDLE_MS, readLast } from './idleLogout';

const web = Platform.OS === 'web';
const UNLOCKED_KEY = 'web:pinUnlocked';
const LAST_USER_KEY = 'web:pinLastUser';
// The one PIN per browser kept before PINs were per user. Whose it was is unknown, so it is dropped.
const UNSCOPED_KEYS = [
  'keychain:one-app-pin',
  'keychain:one-app-pin-setup-completed',
  'keychain:one-app-pin-attempts',
  '@pin_enabled',
  '@pin_skipped',
  'web:pinOwner',
];

const attempt = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback; // storage blocked (private mode, site data off)
  }
};

/** The PIN scope for the signed-in user: their id; during "Login as user", the super-user's who started it. */
export function pinScopeFor(user: { id: string; is_impersonating?: boolean }): string {
  if (!user.is_impersonating) {
    attempt(() => localStorage.setItem(LAST_USER_KEY, user.id), undefined);
    return user.id;
  }
  return attempt(() => localStorage.getItem(LAST_USER_KEY), null) ?? user.id;
}

/** Removes the shared PIN from before PINs were per user: everyone sets theirs again once. */
export function dropUnscopedPIN() {
  if (web) UNSCOPED_KEYS.forEach(key => attempt(() => localStorage.removeItem(key), undefined));
}

/** After a correct PIN or a new PIN: a reload in this tab won't ask this user again. */
export function rememberPINUnlock(scope: string) {
  if (web && scope) attempt(() => sessionStorage.setItem(UNLOCKED_KEY, scope), undefined);
}

/** On lock, sign-out or PIN removal. */
export function forgetPINUnlock() {
  if (web) attempt(() => sessionStorage.removeItem(UNLOCKED_KEY), undefined);
}

/** Whether this tab was unlocked by this user, active recently enough to skip the PIN on load. */
export function wasPINUnlocked(scope: string): boolean {
  if (!web || !scope) return false;
  return attempt(() => sessionStorage.getItem(UNLOCKED_KEY) === scope, false) && Date.now() - readLast() < IDLE_MS;
}
